import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, EnvironmentInjector, PLATFORM_ID } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideClientHydration, withEventReplay, withNoHttpTransferCache } from '@angular/platform-browser';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import {
  CognitoService,
  DateTriggerService,
  bindMyRvBackend,
  cognitoPreferencesStorage,
  hydrateMyRvFromBackend,
  initDraftStore,
  initMyRvStore,
  initPublishedSnapshots,
} from '@cnt-workspace/data-access';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { appRoutes } from './app.routes';
import { jwtInterceptor } from './interceptors/jwt.interceptor';
import { unauthenticatedErrorInterceptor } from './interceptors/unauthenticated-error.interceptor';

// cnt-landing originally adopted curbnturf's existing NgRx state layer
// (listing/boondock/reservation/user, plus transitive alert/network/storage/
// urlControl/addressBook/rating dependencies) as "Phase 0" of a
// server-integration plan, to avoid building a second, parallel NgRx
// architecture. That tradeoff was reversed: NgRx caused real bugs (an
// Actions-stream race in Cognito sign-in — see git history, commit 2290b52;
// a second race in signUp()/completeFederatedSignIn()/updateProfile(),
// removed alongside this), and cnt-landing's own libs never needed most of
// what it registered.
//
// cnt-landing no longer runs an NgRx store, reducers, or effects at all —
// and, as of this pass, no longer imports any real @curbnturf/{auth,network,
// status,user-dashboard} class either. Every shared backend-API service
// cnt-landing calls (listing/site/boondock/message/activity/user/
// reservation/rating/ban-user/notification-feed/contact) has a local,
// HttpClient-only fork in `data-access-cnt/backend-api/`, trimmed to just
// the methods actually used — see that folder's header comment. That
// removed the last real constructor-level dependency on `CachedHttpClient`
// (`@curbnturf/network`), `StatusFacade` (`@curbnturf/status`), and NgRx's
// `Store`/`AuthFacade`, so none of those DI-token overrides are needed here
// anymore either. `CognitoService` (`@curbnturf/auth`) is similarly forked
// into `data-access-cnt/auth/cognito/` — it never had an NgRx dependency,
// the fork just sources its Cognito pool/domain/callback config from
// cnt-landing's own environment instead of `@curbnturf/entities`.

export const appConfig: ApplicationConfig = {
  providers: [
    // P43/A — restore scroll position on back/forward nav
    // (e.g. /search → listing detail → back lands the visitor
    // on the same card they clicked). anchorScrolling enables
    // `#fragment` deep links used by /articles TOC + share bar.
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideHttpClient(withInterceptors([jwtInterceptor, unauthenticatedErrorInterceptor]), withFetch()),
    provideAnimationsAsync(),
    provideIonicAngular({}),
    // HYDRATION LIVES HERE, NOT IN main.ts, AND THAT WAS THE WHOLE BUG.
    //
    // `main.ts` is only the BROWSER entry; the server bootstraps through
    // `app.config.server.ts`, which merges this config with
    // `provideServerRendering(...)`. With the provider in `main.ts` the client
    // asked for hydration while the server emitted no `ngh` annotations to
    // support it — which is literally what NG0505 reports, and why the browser
    // discarded the server-rendered DOM and re-rendered from scratch. That
    // discard is what ate anything typed in the ~100ms window, measured on the
    // wizard at mount 59ms / unmount 125ms / remount 165ms, and still costs
    // /contact and /auth/register typed input today. See F13 / Batch A #14.
    //
    // `withNoHttpTransferCache()` is DELIBERATE and temporary. Hydration turns
    // the HTTP transfer cache on by default, which stops the client re-issuing
    // GETs the server already made — a real second behaviour change in an app
    // that uses no TransferState at all today. Landing both at once would make
    // any regression ambiguous, so the cache is its own commit with its own
    // measurement.
    provideClientHydration(withEventReplay(), withNoHttpTransferCache()),
    // CognitoService is cnt-landing's own fork (data-access-cnt/auth/cognito)
    // of @curbnturf/auth's class — see that folder's header comment.
    CognitoService,
    // Populate the published-snapshot IDB cache before any route resolves so
    // user-published listings hydrate consistently no matter where the user
    // lands first (search, listing detail, hosting, deep link, etc.).
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => () => initPublishedSnapshots(),
    },
    // Same as above, for the in-progress host-wizard draft (and its shelved
    // stack) — moved off localStorage (see draft-store.util.ts) because its
    // quota was too small for a draft carrying several photo data URLs.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => () => initDraftStore(),
    },
    // Eagerly instantiate DateTriggerService so its bookings-stream
    // subscription + 60-min recheck timer are running from app load,
    // not from the first time a feature happens to inject it.
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [DateTriggerService],
      useFactory: () => () => Promise.resolve(),
    },
    // Populate my-rv.util.ts's IndexedDB-backed profile cache before any
    // route resolves — same reasoning as the two initializers above (this
    // key had the same localStorage quota problem, evidenced by its
    // photo-dropping fallback in writeMyRvProfiles). Ordered before the
    // bindMyRvBackend/hydrateMyRvFromBackend initializer below, since that
    // one writes into this same cache and must run after it's hydrated.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => () => initMyRvStore(),
    },
    // Phase 3 — bind my-rv.util.ts's plain functions to real DI (they can't
    // call inject() themselves; every call site invokes them outside a
    // constructor) and pull in the signed-in user's real saved RVs once, so
    // rigs saved on another device show up here too. Deliberately NOT
    // awaited — this is a best-effort background sync, not core
    // functionality, and must never be able to stall app bootstrap.
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [EnvironmentInjector, PLATFORM_ID],
      useFactory: (injector: EnvironmentInjector, platformId: object) => () => {
        bindMyRvBackend(injector);
        void hydrateMyRvFromBackend(platformId);
        return Promise.resolve();
      },
    },
    // Populate the Cognito-session Preferences-backed cache before any route
    // resolves — same reasoning as the storage initializers above, except
    // this one guards actual sign-in state, not just UI cache: without it,
    // AuthService.restoreSession()'s own `await hydrate()` still guarantees
    // correctness (see auth.service.ts), but this initializer is what makes
    // that a no-op wait in the common case instead of a real one.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => () => cognitoPreferencesStorage.hydrate(),
    },
  ],
};
